// v4.3.0 · Diagnóstico inteligente integrado en la ficha del alumno (reemplaza el módulo aparte
// «Diagnósticos Inteligentes»): ICE, zona, perfil, discrepancia, cobertura del CAD, andamiaje y evolución.
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import UnifiedProfileRadar from "@/components/UnifiedProfileRadar";
import { Activity, Loader2, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";

const ZONA_COLORS = { verde: "#1FA971", amarilla: "#E0A106", roja: "#D8434E" };
const CAD_LABELS = {
  aritmetica: "Aritmética", algebra: "Álgebra", geometria: "Geometría",
  razonamiento: "Razonamiento", resolucion: "Resolución",
};
const pretty = (k) => CAD_LABELS[k] || String(k).replace(/_/g, " ");

function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined)
    return <span className="inline-flex items-center gap-1 text-xs text-[#6E7591]"><Minus size={12} /> Sin comparativa</span>;
  const up = delta > 0; const flat = delta === 0;
  const color = flat ? "#6E7591" : up ? "#1FA971" : "#D8434E";
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-1 text-sm font-extrabold" style={{ color }}><Icon size={14} /> {up ? "+" : ""}{delta} pts</span>;
}

// section: "all" | "resumen" (ICE, zona, perfil, andamiaje, historial) | "discrepancia" (radar emoción–cognición)
export default function DiagnosticoPanel({ studentId, section = "all" }) {
  const showResumen = section !== "discrepancia";
  const showDisc = section !== "resumen";
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/diagnostics/students/${studentId}/dashboard`);
      setDetail(data);
    } catch { setDetail({ has_data: false }); } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setBusy(true);
    try {
      await api.post(`/diagnostics/students/${studentId}/diagnostic/compute`);
      toast.success("Métricas calculadas.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron calcular las métricas");
    } finally { setBusy(false); }
  };

  const andam = detail?.andamiaje || {};
  const cov = detail?.cad_coverage;

  return (
    <div className="premium-card p-5 space-y-4" data-testid="diagnostico-panel">
      {showResumen && <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-extrabold text-[#1F2547] flex items-center gap-2"><Activity size={18} className="text-[#A43790]" /> Diagnóstico inteligente</h3>
          <p className="text-xs text-[#6E7591]">ICE, zona, perfil y mapa de andamiaje · Modelo JASQ®</p>
        </div>
        <button onClick={compute} disabled={busy} data-testid="diagnostico-compute-btn"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#A43790] text-[#A43790] text-sm font-bold hover:bg-[#FFE9F0] disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {detail?.has_data ? "Recalcular métricas" : "Calcular métricas"}
        </button>
      </div>}

      {loading && <p className="text-sm text-[#6E7591] py-4 text-center">Cargando…</p>}
      {!loading && detail && !detail.has_data && (
        <p className="text-sm text-[#6E7591] py-4 text-center" data-testid="diagnostico-empty">
          Aún no hay métricas calculadas. Si el alumno ya contestó el CEP y el CAD, pulsa «Calcular métricas».
        </p>
      )}

      {!loading && detail?.has_data && (
        <>
          {showResumen && <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-[#EEF0F6] p-4" data-testid="detail-ice">
              <p className="text-xs font-extrabold uppercase text-[#6E7591]">ICE Global</p>
              <p className="text-3xl font-black" style={{ color: ZONA_COLORS[detail.zona] || "#1F2547" }}>{detail.ice_current ?? "—"}<span className="text-base text-[#6E7591]">/100</span></p>
              <p className="text-[11px] text-[#6E7591]">{detail.zona ? `Zona ${detail.zona}` : ""}{detail.zona_label ? ` · ${detail.zona_label}` : ""}</p>
              <DeltaBadge delta={detail.ice_delta} />
            </div>
            <div className="rounded-xl border border-[#EEF0F6] p-4" data-testid="detail-gap">
              <p className="text-xs font-extrabold uppercase text-[#6E7591]">Cierre de brechas</p>
              <p className="text-3xl font-black text-[#2C49A0]">{detail.gap_closure_rate ?? "—"}{detail.gap_closure_rate != null ? "%" : ""}</p>
              <p className="text-[11px] text-[#6E7591]">áreas fragmentadas superadas</p>
            </div>
            <div className="rounded-xl border border-[#EEF0F6] p-4" data-testid="detail-perfil">
              <p className="text-xs font-extrabold uppercase text-[#6E7591]">Perfil</p>
              <p className="text-2xl font-black text-[#A43790]">{detail.perfil?.code || "—"}</p>
              <p className="text-[11px] text-[#6E7591] leading-tight">{detail.perfil?.label}</p>
            </div>
          </div>

          {cov?.total > 0 && (
            <p className="text-xs text-[#6E7591]" data-testid="diagnostico-cobertura">
              CAD: <b>{cov.answered}</b> de <b>{cov.total}</b> reactivos respondidos
              {cov.score_answered != null ? ` · ${cov.score_answered}% sobre lo respondido` : ""}
              {!cov.complete ? " · evaluación incompleta (lo no respondido es «sin evidencia», no error)" : ""}.
            </p>
          )}

          {(andam.consolidadas?.length || andam.parciales?.length || andam.fragmentadas?.length || andam.sin_evidencia?.length) ? (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="diagnostico-andamiaje">
              {(andam.consolidadas || []).map((a) => <span key={"c" + a.area} className="px-2 py-1 rounded-full bg-[#E9F8EF] text-[#16A34A] font-bold">{pretty(a.area)} · consolidada</span>)}
              {(andam.parciales || []).map((a) => <span key={"p" + a.area} className="px-2 py-1 rounded-full bg-[#FFF7DA] text-[#9C7A18] font-bold">{pretty(a.area)} · parcial</span>)}
              {(andam.fragmentadas || []).map((a) => <span key={"f" + a.area} className="px-2 py-1 rounded-full bg-[#FDECEC] text-[#DC2626] font-bold">{pretty(a.area)} · fragmentada ({a.prioridad})</span>)}
              {(andam.sin_evidencia || []).map((a) => <span key={"s" + a.area} className="px-2 py-1 rounded-full bg-[#F2F4FA] text-[#6E7591] font-bold">{pretty(a.area)} · sin evidencia</span>)}
            </div>
          ) : null}

          </>}

          {showDisc && <div className="rounded-xl border border-[#EEF0F6] p-3" data-testid="radar-unificado-card">
            <h4 className="text-sm font-extrabold text-[#1F2547] flex items-center gap-1.5 mb-1"><Activity size={15} className="text-[#A43790]" /> Discrepancia emoción–cognición</h4>
            <UnifiedProfileRadar cep={detail.radar?.cep || []} cad={detail.radar?.cad || []} />
          </div>}

          {showResumen && detail.history?.length > 1 && (
            <div data-testid="ice-history">
              <h4 className="text-sm font-extrabold text-[#1F2547] mb-2">Historial de ICE</h4>
              <div className="flex flex-wrap gap-2">
                {detail.history.map((h, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border"
                    style={{ borderColor: ZONA_COLORS[h.zona] || "#E1E5F0", color: ZONA_COLORS[h.zona] || "#1F2547" }}>v{h.version}: {h.ice}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
